import {
	css,
	ColorType,
	IPropsWithTheme,
	BaseScaleType,
	FullScaleType,
	EasingType,
	PaddingMixinParamsType,
	MarginMixinParamsType,
	ITheme,
	ThemedCssFunction,
	BreakpointType,
	IScreenMatch,
} from 'styled-components'

import tinycolor from 'tinycolor2'

function wrapCss(fn: (content: ReturnType<typeof css>) => ReturnType<typeof css>): ThemedCssFunction<ITheme> {
	return ((...args: any[]) => fn((css as any)(...args))) as ThemedCssFunction<ITheme>
}

export function breakpoint(breakpointIdentifier: BreakpointType, delta = 0.0) {
	return ({ theme }: IPropsWithTheme): string => `${theme.breakpoints[breakpointIdentifier] + delta}px`
}

export const screen: IScreenMatch = {
	lte: {
		xxs: wrapCss(
			content => css`
				@media screen and (max-width: ${breakpoint('xs', -1.0)}) {
					${content}
				}
			`
		),
		xs: wrapCss(
			content => css`
				@media screen and (max-width: ${breakpoint('sm', -1.0)}) {
					${content}
				}
			`
		),
		sm: wrapCss(
			content => css`
				@media screen and (max-width: ${breakpoint('md', -1.0)}) {
					${content}
				}
			`
		),
		md: wrapCss(
			content => css`
				@media screen and (max-width: ${breakpoint('lg', -1.0)}) {
					${content}
				}
			`
		),
		lg: wrapCss(
			content => css`
				@media screen and (max-width: ${breakpoint('xl', -1.0)}) {
					${content}
				}
			`
		),
		xl: wrapCss(
			content => css`
				@media screen and (max-width: ${breakpoint('xxl', -1.0)}) {
					${content}
				}
			`
		),
	},
	gte: {
		xs: wrapCss(
			content => css`
				@media screen and (min-width: ${breakpoint('xs')}) {
					${content}
				}
			`
		),
		sm: wrapCss(
			content => css`
				@media screen and (min-width: ${breakpoint('sm')}) {
					${content}
				}
			`
		),
		md: wrapCss(
			content => css`
				@media screen and (min-width: ${breakpoint('md')}) {
					${content}
				}
			`
		),
		lg: wrapCss(
			content => css`
				@media screen and (min-width: ${breakpoint('lg')}) {
					${content}
				}
			`
		),
		xl: wrapCss(
			content => css`
				@media screen and (min-width: ${breakpoint('xl')}) {
					${content}
				}
			`
		),
		xxl: wrapCss(
			content => css`
				@media screen and (min-width: ${breakpoint('xxl')}) {
					${content}
				}
			`
		),
	},
}

export function color(colorIdentifier: ColorType) {
	return ({ theme }: IPropsWithTheme): string => {
		if (typeof colorIdentifier === 'string') {
			if (colorIdentifier === 'white') {
				return '#fff'
			} else if (colorIdentifier === 'black') {
				return theme.colors.gray['900']
			} else {
				return theme.colors[colorIdentifier]['500']
			}
		} else {
			return theme.colors[colorIdentifier[0]][colorIdentifier[1]]
		}
	}
}

export function rgbaColor(colorIdentifier: ColorType, alpha = 1.0) {
	return (props: IPropsWithTheme): string => {
		const colorValueFn = color(colorIdentifier)
		const colorObj = tinycolor(colorValueFn(props))
		colorObj.setAlpha(alpha)
		return colorObj.toRgbString()
	}
}

export function margin(...params: MarginMixinParamsType) {
	return ({ theme }: IPropsWithTheme): string =>
		params
			.slice()
			.map(param =>
				typeof param === 'number' || param === 'auto' ? param : `${theme.margins[param as FullScaleType]}px`
			)
			.join(' ')
}

export function padding(...params: PaddingMixinParamsType) {
	return ({ theme }: IPropsWithTheme): string =>
		params
			.slice()
			.map(param => (typeof param === 'number' ? param : `${theme.paddings[param as FullScaleType]}px`))
			.join(' ')
}

export function borderRadius(scaleIdentifier: BaseScaleType) {
	return ({ theme }: IPropsWithTheme): string => `${theme.radius[scaleIdentifier]}px`
}

export function easing(easingIdentifier: EasingType) {
	return ({ theme }: IPropsWithTheme): string => `${theme.easing[easingIdentifier]}`
}

export function fontSize(scaleIdentifier: FullScaleType) {
	return ({ theme }: IPropsWithTheme): string => `${theme.typography.sizes[scaleIdentifier]}px`
}

export function height(scaleIdentifier: FullScaleType) {
	return ({ theme }: IPropsWithTheme): string => `${theme.heights[scaleIdentifier]}px`
}

export function scrollbar(visible = true, size = 8, horizontal = false) {
	return css`
		&::-webkit-scrollbar {
			${horizontal
				? css`
						height: ${visible ? size : 0}px;
				  `
				: css`
						width: ${visible ? size : 0}px;
				  `}
			background-color: ${visible ? color(['gray', '300']) : 'transparent'};
		}
		&::-webkit-scrollbar-thumb {
			background-color: ${visible ? color(['gray', '700']) : 'transparent'};
			border-radius: ${borderRadius('sm')};
		}
	`
}
